/* eslint-disable no-nested-ternary */
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import currency from "currency.js";
import { IAccount, IAdl } from "../../../interfaces/aevo";
import {
  EditWrapper,
  FlagWrapper,
  LinkText,
  MarginWrapper,
  ReferralConfirmationWrapper,
} from "../style";
import { COLORS, TEXT_COLORS } from "../../../constants/design/colors";
import { ToastEnum, ToastStatusEnum, useToast } from "../../../hooks/toast";
import { ReactComponent as Copy } from "../../../assets/svg/copy.svg";
import { ReactComponent as Edit } from "../../../assets/svg/edit.svg";
import { ReactComponent as Cross } from "../../../assets/svg/cross.svg";
import { ReactComponent as Check } from "../../../assets/svg/check.svg";
import { useAccount } from "../../../hooks/api/admin/useAccount";
import Toggle from "../../shared/Toggle";
import ConfirmationModal from "../../shared/ConfirmationModal";
import { shortenAddress } from "../../../utils/strings";
import { IKeyValue, KeyValue } from ".";
import {
  AssetBalanceValue,
  AssetBreakdownEmpty,
  AssetBreakdownHeader,
  AssetBreakdownRow,
  AssetBreakdownTable,
  AssetBreakdownWrapper,
  AssetIdentity,
  AssetLogo,
  AssetLogoFallback,
  AssetName,
  ConfiscateButton,
  KeyValueContainer,
  StatTitle,
} from "./style";
import { DataContext } from "../../../context/DataContext";
import { TextWrapper, CopyButton } from "../../Table/style";
import { Button, ButtonThemeEnum } from "../../shared/Buttons/styles";
import { RequestWrapper } from "../../Withdrawals/style";
import { useManualModeAccounts } from "../../../hooks/api/admin/useManualModeAccounts";
import { Spinner } from "../../shared/Spinner";
import { getCollateralLogo } from "../../../utils/asset";
import { SPACING } from "../../../constants/design/spacing";
import { useReferral } from "../../../hooks/api/admin/useReferral";
import { useQueryAccounts } from "../../../hooks/api/admin/useQueryAccounts";
import { QueryTypeEnum } from "../../Dropdown/DashboardFilterDropdown";
import { useAccountDegenTransactions } from "../../../hooks/api/admin/useAccountDegenTransactions";
import { DEGEN_ACCOUNT_ADDRESS } from "../../../constants/admin";
import { useAccountLatency } from "../../../hooks/api/admin/useAccountLatency";
import { FundingEvidence } from "../FundingEvidence";

interface IAccountStatsProps {
  data?: IAccount;
  isMobile?: boolean;
}

const formatAssetAmount = (value: string) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "-";

  return amount.toLocaleString("en-US", {
    maximumFractionDigits: 6,
  });
};

function CollateralLogo({ asset }: { asset: string }) {
  const [failed, setFailed] = useState(false);
  const logo = getCollateralLogo(asset);

  if (!logo || failed) {
    return (
      <AssetLogoFallback aria-hidden="true">
        {asset.slice(0, 1).toUpperCase()}
      </AssetLogoFallback>
    );
  }

  return (
    <AssetLogo
      src={logo}
      alt=""
      aria-hidden="true"
      onError={() => setFailed(true)}
    />
  );
}

export function AccountStats({ data, isMobile }: IAccountStatsProps) {
  const { accounts, records, address: currentAccountAddress } = useContext(DataContext);
  const { addToast } = useToast();
  const { queryAccount } = useQueryAccounts();
  const { setManualMode } = useManualModeAccounts();
  const { createReferral } = useReferral();
  const { data: degenTxsData } = useAccountDegenTransactions();
  const { data: latencyData, isLoading: isLatencyLoading, updateAccountLatency } = useAccountLatency(data?.account);
  const [isEdit, setEdit] = useState<boolean>(false);
  const [isEditRefund, setEditRefund] = useState<boolean>(false);
  const [isEditRecover, setEditRecover] = useState<boolean>(false);
  const [isEditReferralBonus, setEditReferralBonus] = useState<boolean>(false);
  const [isEditRefereeDiscount, setEditRefereeDiscount] =
    useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [username, setUsername] = useState<string>(data?.username || "");
  const [referralBonus, setReferralBonus] = useState<number>();
  const [refereeDiscount, setRefereeDiscount] = useState<number>();
  const [isValidUsername, setIsValidUsername] = useState<boolean>(true);
  const [refundAmount, setRefundAmount] = useState<string>("");
  const [recoverAmount, setRecoverAmount] = useState<string>("");
  const [isValidGroup, setIsValidGroup] = useState<boolean>(true);
  const [showBlacklistModal, setShowBlacklistModal] = useState<boolean>(false);
  const [showReferralModal, setShowReferralModal] = useState<boolean>(false);
  const [group, setGroup] = useState<string>(data?.user_group || "");
  const {
    updateUsername,
    refundAccount,
    recoverAccount,
    confiscateBalance,
    mutate,
    updatePortfolioMargin,
    updateReferralBonus,
    updateRefereeDiscount,
    updateUserGroup,
  } = useAccount();
  const [showConfiscateModal, setShowConfiscateModal] =
    useState<boolean>(false);
  const [confiscateCollateral, setConfiscateCollateral] = useState<string>("");
  const [confiscateAmount, setConfiscateAmount] = useState<string>("");
  const [confiscateCounterparty, setConfiscateCounterparty] =
    useState<string>("");
  const [referralReferrer, setReferralReferrer] = useState<string>("");
  const [referralReferee, setReferralReferee] = useState<string>(
    data?.account || ""
  );
  const [referralRefereeDiscount, setReferralRefereeDiscount] =
    useState<number>(0.1);
  const [referralReferralBonus, setReferralReferralBonus] =
    useState<number>(0.1);
  const [showPMModal, setShowPMModal] = useState<boolean>(false);
  const [isEditMakerLatency, setEditMakerLatency] = useState<boolean>(false);
  const [isEditTakerLatency, setEditTakerLatency] = useState<boolean>(false);
  const [makerLatency, setMakerLatency] = useState<string>("");
  const [takerLatency, setTakerLatency] = useState<string>("");

  const isDegenAccount = data?.account?.toLowerCase() === DEGEN_ACCOUNT_ADDRESS.toLowerCase();

  const degenStats = useMemo(() => {
    if (!isDegenAccount || !degenTxsData) return null;

    return {
      totalDeposit: {
        value: currency(data?.equity!).format(),
        label: "Total Deposits",
        description: "Cumulative sum of all deposit transaction amounts. Shows total user inflow."
      },
      ...degenTxsData.stats,
    };
  }, [isDegenAccount, degenTxsData]);

  const transfersWarning =
    "(Only click the green submission tick once, and wait for a toast success. If it fails or nothing happens, reach out to an engineer)";

  useEffect(() => {
    if (isEdit === false) setUsername(data?.username || "");
  }, [data?.username, isEdit]);

  const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newUsername = event.target.value;
    setIsValidUsername(!newUsername.includes(" "));
    setUsername(newUsername);
  }, []);

  const onChangeGroup = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newGroup = event.target.value;
      setIsValidGroup(!newGroup.includes(" "));
      setGroup(newGroup);
    },
    []
  );

  const onChangeRefund = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newRefund = event.target.value;
      setRefundAmount(newRefund);
    },
    []
  );

  const onChangeRecover = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newRecover = event.target.value;
      setRecoverAmount(newRecover);
    },
    []
  );

  const onChangeReferralBonus = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newReferralBonus = event.target.value;
      setReferralBonus(Number(newReferralBonus));
    },
    []
  );

  const onChangeRefereeDiscount = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newRefereeDiscount = event.target.value;
      console.log(newRefereeDiscount);
      setRefereeDiscount(Number(newRefereeDiscount));
    },
    []
  );

  const onSave = useCallback(async () => {
    if (isValidUsername) {
      setLoading(true);
      await updateUsername(username).finally(() => {
        mutate();
        setLoading(false);
        setEdit(false);
      });
    } else {
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: "Username should not contain spaces",
          status: ToastStatusEnum.ERROR,
        },
        4000
      );
    }
  }, [isValidUsername, updateUsername, username, mutate, addToast]);

  const onSaveGroup = useCallback(async () => {
    if (isValidGroup) {
      setLoading(true);
      await updateUserGroup(group).finally(() => {
        mutate();
        setLoading(false);
        setEdit(false);
        setIsValidGroup(true);
      });
    } else {
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: "Group should not contain spaces",
          status: ToastStatusEnum.ERROR,
        },
        4000
      );
    }
  }, [isValidGroup, updateUserGroup, group, mutate, addToast]);

  const onSaveRefund = useCallback(async () => {
    setLoading(true);
    if (refundAmount) {
      await refundAccount(refundAmount).finally(() => {
        mutate();
        setLoading(false);
        setEditRefund(false);
        setRefundAmount("");
      });
    }
  }, [refundAmount, refundAccount, mutate]);

  const onSaveRecover = useCallback(async () => {
    setLoading(true);
    if (recoverAmount) {
      await recoverAccount(recoverAmount).finally(() => {
        mutate();
        setLoading(false);
        setEditRecover(false);
        setRecoverAmount("");
      });
    }
  }, [recoverAmount, recoverAccount, mutate]);

  const onSaveReferralBonus = useCallback(async () => {
    setLoading(true);
    if (referralBonus) {
      await updateReferralBonus(referralBonus).finally(() => {
        mutate();
        setLoading(false);
        setEditReferralBonus(false);
      });
    }
  }, [mutate, referralBonus, updateReferralBonus]);

  const onSaveRefereeDiscount = useCallback(async () => {
    setLoading(true);
    if (refereeDiscount !== undefined) {
      await updateRefereeDiscount(refereeDiscount).finally(() => {
        mutate();
        setLoading(false);
        setEditRefereeDiscount(false);
      });
    }
  }, [mutate, refereeDiscount, updateRefereeDiscount]);

  const onChangeMakerLatency = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      if (value === "" || /^\d+$/.test(value)) {
        setMakerLatency(value);
      }
    },
    []
  );

  const onChangeTakerLatency = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      if (value === "" || /^\d+$/.test(value)) {
        setTakerLatency(value);
      }
    },
    []
  );

  const onSaveMakerLatency = useCallback(async () => {
    if (makerLatency === "") return;
    setLoading(true);
    await updateAccountLatency(Number(makerLatency), undefined).finally(() => {
      setLoading(false);
      setEditMakerLatency(false);
      setMakerLatency("");
    });
  }, [makerLatency, updateAccountLatency]);

  const onSaveTakerLatency = useCallback(async () => {
    if (takerLatency === "") return;
    setLoading(true);
    await updateAccountLatency(undefined, Number(takerLatency)).finally(() => {
      setLoading(false);
      setEditTakerLatency(false);
      setTakerLatency("");
    });
  }, [takerLatency, updateAccountLatency]);

  const onPM = useCallback(
    async (isEnabled: boolean) => {
      setLoading(true);
      await updatePortfolioMargin(isEnabled).finally(() => {
        mutate();
        setLoading(false);
        setShowPMModal(false);
      });
    },
    [mutate, updatePortfolioMargin]
  );

  const onBlacklist = useCallback(() => {
    setShowBlacklistModal(true);
  }, []);
  const onCopy = useCallback(
    (address: string) => {
      navigator.clipboard.writeText(address);
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: "Address Copied",
          status: ToastStatusEnum.SUCCESS,
        },
        4000
      );
    },
    [addToast]
  );

  const getAirtableTag = useCallback(
    (address: string) => records[address] || undefined,
    [records]
  );

  const referrerDetails = useCallback(
    (account: string) => {
      const referrer: IAccount | undefined = accounts.find(
        (acc) => acc.account === account
      );

      if (referrer?.account) {
        return getAirtableTag(referrer?.account);
      }

      return shortenAddress(account);
    },
    [accounts, getAirtableTag]
  );

  const [referrerAddress, setReferrerAddress] = useState<string>();
  useEffect(() => {
    const fetchReferrerData = async () => {
      if (data?.referrer) {
        const queryAccounts = await queryAccount(
          QueryTypeEnum.USERNAME,
          data.referrer
        );
        if (queryAccounts?.length === 1) {
          setReferrerAddress(queryAccounts[0].account);
        }
      }
    };

    fetchReferrerData();
  }, [data?.referrer, queryAccount, referrerAddress]);

  const stats: IKeyValue[] = useMemo(
    () => [
      {
        title: "Airtable Tag",
        value: (
          <TextWrapper>
            <div>
              {data?.account && getAirtableTag(data?.account)
                ? getAirtableTag(data?.account)
                : "-"}
            </div>
          </TextWrapper>
        ),
      },
      {
        title: "Account Address",
        value: (
          <TextWrapper>
            <div>{shortenAddress(data?.account!)}</div>
            <CopyButton
              onClick={(e) => {
                e.stopPropagation();
                onCopy(data?.account || "");
              }}
              style={{ padding: 0 }}
            >
              <Copy />
            </CopyButton>
          </TextWrapper>
        ),
      },
      {
        title: "Maker Latency (ms)",
        value: isLatencyLoading ? (
          <TextWrapper>
            <span>Loading...</span>
          </TextWrapper>
        ) : isEditMakerLatency ? (
          <EditWrapper>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={makerLatency}
              onChange={onChangeMakerLatency}
              placeholder={latencyData?.maker_latency_ms?.toString() || "0"}
            />
            <Check onClick={() => onSaveMakerLatency()} />
            <Cross
              onClick={() => {
                setEditMakerLatency(false);
                setMakerLatency("");
              }}
            />
          </EditWrapper>
        ) : (
          <TextWrapper>
            <span>{latencyData?.maker_latency_ms ?? "-"}</span>
            <Edit onClick={() => setEditMakerLatency(true)} />
          </TextWrapper>
        ),
      },
      {
        title: "Taker Latency (ms)",
        value: isLatencyLoading ? (
          <TextWrapper>
            <span>Loading...</span>
          </TextWrapper>
        ) : isEditTakerLatency ? (
          <EditWrapper>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={takerLatency}
              onChange={onChangeTakerLatency}
              placeholder={latencyData?.taker_latency_ms?.toString() || "0"}
            />
            <Check onClick={() => onSaveTakerLatency()} />
            <Cross
              onClick={() => {
                setEditTakerLatency(false);
                setTakerLatency("");
              }}
            />
          </EditWrapper>
        ) : (
          <TextWrapper>
            <span>{latencyData?.taker_latency_ms ?? "-"}</span>
            <Edit onClick={() => setEditTakerLatency(true)} />
          </TextWrapper>
        ),
      },
      {
        title: "Username",
        value: isEdit ? (
          <EditWrapper>
            <input value={username} onChange={onChange} />
            <Check onClick={() => onSave()} />
            <Cross onClick={() => setEdit(false)} />
          </EditWrapper>
        ) : (
          <TextWrapper>
            <span>{data?.username}</span> <Edit onClick={() => setEdit(true)} />
          </TextWrapper>
        ),
      },
      {
        title: "Portfolio Margin",
        value: (
          <MarginWrapper>
            <Toggle
              isOn={Boolean(data?.is_portfolio)}
              onToggle={() => setShowPMModal(true)}
            />
          </MarginWrapper>
        ),
      },
      {
        title: "Account Type",
        value: data?.account_type.toString().toUpperCase(),
      },
      {
        title: "Referrer",
        value: data?.referrer ? (
          <LinkText
            to={data?.referrer ? `/account/${referrerAddress}` : ""}
            style={{
              color: TEXT_COLORS.one,
            }}
            target="_blank"
          >
            {data?.referrer ? referrerDetails(data?.referrer!) : "-"}
          </LinkText>
        ) : (
          <TextWrapper>
            <span>-</span> <Edit onClick={() => setShowReferralModal(true)} />
          </TextWrapper>
        ),
      },
      {
        title: "Email",
        value: data?.email_address || "-",
      },
      {
        title: "Permissions",
        value: data?.permissions.length ? data.permissions.join("\n") : "-",
      },
      {
        title: "Referral Bonus",
        value: isEditReferralBonus ? (
          <EditWrapper>
            <input
              type="number"
              value={referralBonus}
              onChange={onChangeReferralBonus}
            />
            <Check onClick={() => onSaveReferralBonus()} />
            <Cross
              onClick={() => {
                setEditReferralBonus(false);
                setReferralBonus(Number(data?.referral_bonus || "0"));
              }}
            />
          </EditWrapper>
        ) : (
          <TextWrapper>
            <span>{data?.referral_bonus}</span>{" "}
            <Edit onClick={() => setEditReferralBonus(true)} />
          </TextWrapper>
        ),
      },
      {
        title: "Referee Discount",
        value: isEditRefereeDiscount ? (
          <EditWrapper>
            <input
              type="number"
              value={refereeDiscount}
              onChange={onChangeRefereeDiscount}
            />
            <Check onClick={() => onSaveRefereeDiscount()} />
            <Cross
              onClick={() => {
                setEditRefereeDiscount(false);
                setRefereeDiscount(Number(data?.referee_discount || "0"));
              }}
            />
          </EditWrapper>
        ) : (
          <TextWrapper>
            <span>{data?.referee_discount}</span>{" "}
            <Edit onClick={() => setEditRefereeDiscount(true)} />
          </TextWrapper>
        ),
      },
      {
        title: "User Group",
        value: isEdit ? (
          <EditWrapper>
            <input value={group} onChange={onChangeGroup} />
            <Check onClick={() => onSaveGroup()} />
            <Cross onClick={() => setEdit(false)} />
          </EditWrapper>
        ) : (
          <TextWrapper>
            <span>{data?.user_group || ""}</span>{" "}
            <Edit onClick={() => setEdit(true)} />
          </TextWrapper>
        ),
      },
      {
        title: "In Liquidation",
        value: data?.in_liquidation ? "Yes" : "No",
      },
      {
        title: "In ADL",
        value: data?.in_adl.length
          ? data.in_adl.map((adl: IAdl) => adl.instrument_name).join(", ")
          : "No",
      },
      {
        title: "Blacklist Account",
        value: (
          <FlagWrapper>
            <Button
              buttonTheme={ButtonThemeEnum.NEGATIVE}
              style={{ maxHeight: 20, minHeight: 20, fontSize: 11 }}
              onClick={() => onBlacklist()}
            >
              Blacklist
            </Button>
          </FlagWrapper>
        ),
      },
    ],
    [
      data?.account,
      data?.account_type,
      data?.email_address,
      data?.in_liquidation,
      data?.in_adl,
      data?.is_portfolio,
      data?.permissions,
      data?.referee_discount,
      data?.referral_bonus,
      data?.referrer,
      data?.user_group,
      data?.username,
      getAirtableTag,
      group,
      isEdit,
      isEditMakerLatency,
      isEditRefereeDiscount,
      isEditReferralBonus,
      isEditTakerLatency,
      isLatencyLoading,
      latencyData?.maker_latency_ms,
      latencyData?.taker_latency_ms,
      makerLatency,
      onChange,
      onChangeGroup,
      onChangeMakerLatency,
      onChangeRefereeDiscount,
      onChangeReferralBonus,
      onChangeTakerLatency,
      onCopy,
      onBlacklist,
      onSave,
      onSaveGroup,
      onSaveMakerLatency,
      onSaveRefereeDiscount,
      onSaveReferralBonus,
      onSaveTakerLatency,
      refereeDiscount,
      referralBonus,
      referrerAddress,
      referrerDetails,
      takerLatency,
      username,
    ]
  );

  const equityStats: IKeyValue[] = useMemo(
    () => [
      {
        title: "Equity",
        value: currency(data?.equity!).format(),
      },
      {
        title: "Available Balance",
        value: currency(data?.available_balance!).format(),
      },
      {
        title: "Available Margin",
        value: currency(data?.available_margin!).format(),
      },
      {
        title: "Credits",
        value: currency(data?.credit!).format(),
      },
      {
        title: `Refund Account ${transfersWarning}`,
        value: isEditRefund ? (
          <EditWrapper>
            <input
              type="number"
              value={refundAmount}
              onChange={onChangeRefund}
            />
            <Check onClick={() => onSaveRefund()} />
            <Cross
              onClick={() => {
                setEditRefund(false);
                setRefundAmount("");
              }}
            />
          </EditWrapper>
        ) : (
          <TextWrapper>
            <span />
            <Edit onClick={() => setEditRefund(true)} />
          </TextWrapper>
        ),
      },
      {
        title: `Recover Account ${transfersWarning}`,
        value: isEditRecover ? (
          <EditWrapper>
            <input
              type="number"
              value={recoverAmount}
              onChange={onChangeRecover}
            />
            <Check onClick={() => onSaveRecover()} />
            <Cross
              onClick={() => {
                setEditRecover(false);
                setRecoverAmount("");
              }}
            />
          </EditWrapper>
        ) : (
          <TextWrapper>
            <span />
            <Edit onClick={() => setEditRecover(true)} />
          </TextWrapper>
        ),
      },
    ],
    [
      data?.available_balance,
      data?.available_margin,
      data?.credit,
      data?.equity,
      isEditRefund,
      isEditRecover,
      onChangeRefund,
      onChangeRecover,
      onSaveRefund,
      onSaveRecover,
      refundAmount,
      recoverAmount,
    ]
  );

  const sortedCollaterals = useMemo(
    () =>
      [...(data?.collaterals || [])].sort((left, right) => {
        const valueDifference =
          Number(right.collateral_value) - Number(left.collateral_value);

        return (
          valueDifference ||
          left.collateral_asset.localeCompare(right.collateral_asset)
        );
      }),
    [data?.collaterals]
  );

  if (!data?.account) return null;
  return (
    <div>
      <ConfirmationModal
        isLoading={isLoading}
        title={"Change Margin Type"}
        show={showPMModal}
        onHide={() => setShowPMModal(false)}
        style={{ width: "311px" }}
        extras={<div>Are you sure you want to change the margin type?</div>}
        primaryColor="blue"
        confirmationButton={{
          title: `Switch to ${data?.is_portfolio ? "Standard" : "Portfolio"
            } Margin`,
          onClick: () => onPM(!data?.is_portfolio),
          disabled: isLoading,
        }}
      />
      <ConfirmationModal
        width={"280px"}
        primaryColor={COLORS.red.one}
        secondaryColor={COLORS.red.two}
        tertiaryColor={COLORS.red.three}
        title={"Confiscate Balance"}
        isLoading={isLoading}
        extras={
          <ReferralConfirmationWrapper>
            <RequestWrapper>
              This will{" "}
              <span style={{ color: COLORS.red.one }}>confiscate</span>{" "}
              {confiscateCollateral} from{" "}
              <div style={{ color: COLORS.blue.one }}>{data?.account}</div>
            </RequestWrapper>
            <div>
              Amount ({confiscateCollateral}):{" "}
              <input
                value={confiscateAmount}
                onChange={(e) => setConfiscateAmount(e.target.value)}
              />
            </div>
            <div>
              Counterparty:{" "}
              <input
                value={confiscateCounterparty}
                onChange={(e) => setConfiscateCounterparty(e.target.value)}
              />
            </div>
          </ReferralConfirmationWrapper>
        }
        show={showConfiscateModal}
        onHide={() => setShowConfiscateModal(false)}
        confirmationButton={{
          title: "Confiscate",
          onClick: async () => {
            if (data?.account) {
              setLoading(true);
              await confiscateBalance(
                confiscateAmount,
                confiscateCounterparty,
                confiscateCollateral
              ).finally(() => {
                setLoading(false);
                setShowConfiscateModal(false);
                setConfiscateAmount("");
                setConfiscateCounterparty("");
                mutate();
              });
            }
          },
          disabled:
            isLoading ||
            !data?.account ||
            !confiscateAmount ||
            !confiscateCounterparty ||
            !confiscateCollateral,
        }}
      />
      <ConfirmationModal
        width={"200px"}
        primaryColor={COLORS.red.one}
        secondaryColor={COLORS.red.two}
        tertiaryColor={COLORS.red.three}
        title={"Blacklist Account"}
        extras={
          <RequestWrapper>
            This will <span style={{ color: COLORS.red.one }}>blacklist</span>{" "}
            the account{" "}
            <div
              style={{
                color: records[data?.account!]
                  ? COLORS.blue.one
                  : TEXT_COLORS.one,
              }}
            >
              {records[data?.account!]
                ? records[data?.account!]
                : data?.account!}
            </div>
          </RequestWrapper>
        }
        show={showBlacklistModal}
        onHide={() => setShowBlacklistModal(false)}
        confirmationButton={{
          title: "Blacklist",
          onClick: async () => {
            if (data?.account) {
              setManualMode(data?.account, true, "blacklist").finally(() => {
                setShowBlacklistModal(false);
              });
            }
          },
          disabled: isLoading || !data?.account,
        }}
      />
      <ConfirmationModal
        width={"200px"}
        primaryColor={COLORS.blue.one}
        secondaryColor={COLORS.blue.two}
        tertiaryColor={COLORS.blue.three}
        title={"Create Referral"}
        extras={
          <ReferralConfirmationWrapper>
            <div>
              Referrer:{" "}
              <input
                value={referralReferrer}
                onChange={(e) => setReferralReferrer(e.target.value)}
              />
            </div>
            <div>
              Referee:{" "}
              <input
                value={referralReferee}
                onChange={(e) => setReferralReferee(e.target.value)}
              />
            </div>
            <div>
              ReferralBonus:{" "}
              <input
                value={referralReferralBonus}
                onChange={(e) =>
                  setReferralReferralBonus(Number(e.target.value))
                }
              />
            </div>
            <div>
              RefereeDiscount:{" "}
              <input
                value={referralRefereeDiscount}
                onChange={(e) =>
                  setReferralRefereeDiscount(Number(e.target.value))
                }
              />
            </div>
          </ReferralConfirmationWrapper>
        }
        show={showReferralModal}
        onHide={() => setShowReferralModal(false)}
        confirmationButton={{
          title: "Create",
          onClick: async () => {
            if (data?.account) {
              createReferral(
                referralReferrer,
                referralReferee,
                referralReferralBonus,
                referralRefereeDiscount
              ).finally(() => {
                setShowReferralModal(false);
              });
            }
          },
          disabled:
            !data?.account ||
            !referralReferrer ||
            !referralReferee ||
            !referralReferralBonus ||
            !referralRefereeDiscount,
        }}
      />
      <StatTitle isMobile={isMobile}>Account</StatTitle>
      <KeyValueContainer isMobile={isMobile}>
        {stats.map((stat, i) => (
          <KeyValue key={i} title={stat.title} value={stat.value} />
        ))}
      </KeyValueContainer>
      <FundingEvidence data={data} requestedAccount={currentAccountAddress} />
      <StatTitle isMobile={isMobile}>Asset Breakdown</StatTitle>
      <AssetBreakdownWrapper isMobile={isMobile}>
        {isLoading ? (
          <Spinner />
        ) : data && data.account ? (
          <AssetBreakdownTable>
            <AssetBreakdownHeader>
              <span>Asset</span>
              <span>Balance</span>
              <span>Available</span>
              <span>USD value</span>
              <span aria-hidden="true" />
            </AssetBreakdownHeader>
            {sortedCollaterals.map((collateral) => (
              <AssetBreakdownRow key={collateral.collateral_asset}>
                <AssetIdentity>
                  <CollateralLogo asset={collateral.collateral_asset} />
                  <AssetName title={collateral.collateral_asset}>
                    {collateral.collateral_asset}
                  </AssetName>
                </AssetIdentity>
                <AssetBalanceValue title={collateral.balance}>
                  {formatAssetAmount(collateral.balance)}
                </AssetBalanceValue>
                <AssetBalanceValue
                  tone="available"
                  title={collateral.available_balance}
                >
                  {formatAssetAmount(collateral.available_balance)}
                </AssetBalanceValue>
                <AssetBalanceValue
                  tone="value"
                  title={collateral.collateral_value}
                >
                  {currency(collateral.collateral_value).format()}
                </AssetBalanceValue>
                <ConfiscateButton
                  type="button"
                  onClick={() => {
                    setConfiscateCollateral(collateral.collateral_asset);
                    setConfiscateAmount("");
                    setConfiscateCounterparty("");
                    setShowConfiscateModal(true);
                  }}
                >
                  Confiscate
                </ConfiscateButton>
              </AssetBreakdownRow>
            ))}
          </AssetBreakdownTable>
        ) : (
          <AssetBreakdownEmpty>No collateral balances.</AssetBreakdownEmpty>
        )}
      </AssetBreakdownWrapper>
      {isDegenAccount && degenStats && (
        <>
          <StatTitle isMobile={isMobile}>Degen Stats</StatTitle>
          <KeyValueContainer isMobile={isMobile}>
            {degenStats.totalDeposit && (
              <KeyValue
                title={degenStats.totalDeposit.label}
                value={
                  <div>
                    <div>{currency(degenStats.totalDeposit.value).format()}</div>
                    <div style={{ fontSize: 10, color: TEXT_COLORS.two, marginTop: SPACING.one }}>
                      {degenStats.totalDeposit.description}
                    </div>
                  </div>
                }
              />
            )}
            {degenStats.totalWithdraw && (
              <KeyValue
                title={degenStats.totalWithdraw.label}
                value={
                  <div>
                    <div>{currency(degenStats.totalWithdraw.value).format()}</div>
                    <div style={{ fontSize: 10, color: TEXT_COLORS.two, marginTop: SPACING.one }}>
                      {degenStats.totalWithdraw.description}
                    </div>
                  </div>
                }
              />
            )}
            {degenStats.equity && (
              <KeyValue
                title={degenStats.equity.label}
                value={
                  <div>
                    <div>{currency(degenStats.equity.value).format()}</div>
                    <div style={{ fontSize: 10, color: TEXT_COLORS.two, marginTop: SPACING.one }}>
                      {degenStats.equity.description}
                    </div>
                  </div>
                }
              />
            )}
            {degenStats.asset && (
              <KeyValue
                title={degenStats.asset.label}
                value={
                  <div>
                    <div>{currency(degenStats.asset.value).format()}</div>
                    <div style={{ fontSize: 10, color: TEXT_COLORS.two, marginTop: SPACING.one }}>
                      {degenStats.asset.description}
                    </div>
                  </div>
                }
              />
            )}
            {degenStats.liability && (
              <KeyValue
                title={degenStats.liability.label}
                value={
                  <div>
                    <div>-{currency(degenStats.liability.value).format()}</div>
                    <div style={{ fontSize: 10, color: TEXT_COLORS.two, marginTop: SPACING.one }}>
                      {degenStats.liability.description}
                    </div>
                  </div>
                }
              />
            )}
          </KeyValueContainer>
        </>
      )}
      <StatTitle isMobile={isMobile}>Equity</StatTitle>
      <KeyValueContainer isMobile={isMobile}>
        {equityStats.map((stat, i) => (
          <KeyValue key={i} title={stat.title} value={stat.value} />
        ))}
      </KeyValueContainer>
    </div>
  );
}
